import * as React from 'react';
import { Upload, Message, Button } from '@bedrock/components';
import { Eye } from '@bedrock/icons-react';
import type {
  UploadFile,
  BrFile,
  UploadChangeParam,
  UploadProps,
} from '@bedrock/components/lib/Upload/types';
import { apiService, defs, enums } from '#services';
import { downloadFile } from '#utils/download';
import classNames from "classnames/bind";
import s from './S3Upload.less';
import {cloneDeep} from 'lodash'
import { WithViewBaseConfigProvider } from '#features/PxToRemWrapper';

const cx = classNames.bind(s);

const FILE_SIZE_LIMIT = 200 * 1024 * 1024;

export type TUploadValue = { id: number; name: string; size: number, uid?: string };

export type TUploadFileMap = Record<
    string,
    TUploadValue & { uid?: string; token?: string; completed?: boolean }
>;

export const S3Upload = (
    props: Omit<UploadProps, 'onChange'> & {
      view?: boolean;
      source?: defs.FileUploadSource;
      value?: TUploadValue[];
      onChange?: (v: TUploadValue[]) => void;
    }
) => {
  const { limit, className, source = enums.FileUploadSourceEnum.CS_MAIN_SUPPLIER, value = [], onChange, ...restProps } = props;
  const propFileList = useCreation(
      () =>
          value.map<UploadFile>((v) => ({
            ...v,
            uid: v.uid || `${v.id}`,
            status: 'done',
          })),
      [value]
  );
  const fileMap = useCreation<TUploadFileMap>(
      (prev) => propFileList.reduce((r, v) => ({ ...r, [v.uid]: v as TUploadFileMap[keyof TUploadFileMap] }), prev || {}),
      [propFileList]
  );
  const [fileList, setFileList] = React.useState<UploadFile[]>([]);

  React.useEffect(() => {
    if (props.view) {
      // 只有查看模式,以value展示为主
      setFileList(propFileList)
    } else {
      // 如果上层现有已上传好的文件数据，就更新添加到文件列表中，并放置到最前面
      const initFileList = propFileList.filter(v => !fileList.find(t => t.uid === v.uid));
      initFileList.length > 0 && setFileList([...initFileList, ...fileList])
    }
  }, [propFileList])

  const getAction = async (file: BrFile) => {
    const resUpload = await apiService.fileRequestUploadFile({
      file_name: file.name,
      file_size: file.size,
      source,
    });
    const { token, upload_url } = resUpload?.data || {};

    if (!upload_url) throw new Error('upload_url is null');

    fileMap[file.uid] = {
      uid: file.uid,
      id: 0,
      name: file.name,
      size: file.size,
      token,
      filepickering: false,
      completed: false,
    };

    return upload_url;
  };

  const doChange = async (e: UploadChangeParam) => {

    let fileList = cloneDeep(e.fileList)
    // 更新文件列表
    setFileList(fileList)

    // 找出上传成功的文件
    const succeedFiles = fileList
        .filter((t) => t.status === 'done')
        .map((t) => fileMap[t.uid])
        .filter(Boolean);

    const changedFiles = succeedFiles.map((v) => ({
      id: v.id,
      name: v.name,
      size: v.size,
      uid: v.uid
    }));
    onChange?.(changedFiles);
  };


  // filepicker更新
  React.useEffect(() => {
    Object.values(fileMap).forEach((item) => {
      if (!item.id && !item.completed && item.token) {
        item.completed = true;
        apiService.fileCompleteUploadFile({
          token: item.token,
        }).then(res => {
          res && (item.id = res?.data?.id!);
          onChange?.(Object.values(fileMap).filter(v => v.id && fileList.find(n => n?.id === v.id)).map(v => ({ id: v.id, name: v.name, size: v.size, uid: v.uid })));
        });
      }
    });
  }, [fileList]);


  const onError = (err, file) => {
    if (err.message === 'Exceed Limit') {
      Message.error(`文件限制最多上传${limit}个`);
    } else if (err.message === 'Size Limit' && file) {
      Message.error(`文件${file.name}大小超出限制`);
    } else if (err.message === 'Network Error') {
      Message.error(`网络连接断开`);
    }
  };

  console.log('fileList', fileList, restProps, value)

  return (
    <WithViewBaseConfigProvider>
      <Upload
          className={cx('sfs3u', { 'file-list-readonly': props.view }, className)}
          action={getAction}
          urlPath={() => ''}
          // s3上传得Blob或者File对象包装一下，后者可以定义名称
          beforeUpload={(v) => new File([v], v.name)}
          format="raw"
          method="PUT"
          limit={limit}
          fileList={fileList}
          showRemoveIconWhenError
          fileSizeLimit={FILE_SIZE_LIMIT}
          onError={onError}
          onChange={doChange}
          renderIcon={(v) =>
              // 只有图片和PDF文件才显示预览按钮
              /(png|jpe?g|pdf)$/.test(v.name || '') && (
                  <Button
                      type="text-subtle"
                      size="xSmall"
                      icon={<Eye />}
                      onClick={async () => {
                        const url = await apiService.fileGetPreviewUrl({
                          file_id: fileMap[v.uid].id,
                        });
                        url?.data && window.open(url.data);
                      }}
                  />
              )
          }
          onDownload={async (v) => {
            const url = await apiService.fileGetDownloadUrl({
              file_id: fileMap[v.uid].id,
            });
            url?.data && downloadFile(url.data, v.name);
          }}
          {...restProps}
      />
    </WithViewBaseConfigProvider>
  );
};

function useCreation<T>(factory: (prev: T) => T, deps: React.DependencyList) {
  const { current } = React.useRef({
    deps,
    obj: undefined as undefined | T,
    initialized: false,
  });
  if (current.initialized === false || !depsAreSame(current.deps, deps)) {
    current.deps = deps;
    current.obj = factory(current.obj);
    current.initialized = true;
  }
  return current.obj as T;
}

function depsAreSame(oldDeps: React.DependencyList, deps: React.DependencyList): boolean {
  if (oldDeps === deps) return true;
  for (let i = 0; i < oldDeps.length; i++) {
    if (!Object.is(oldDeps[i], deps[i])) return false;
  }
  return true;
}